import { Box, Button, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import React, { useCallback, useMemo } from 'react';

import { HorizontalCard } from '../../../components/_mui';
import { NOT_SELECTED_TEXT } from '../../../constants/marketplace';
import { ICampaignItem } from '../../../types/pyg';

interface IProps {
  item: ICampaignItem;
  pickedOptionId?: string;
  onViewDetails: (item: ICampaignItem) => void;
  onOpenAccordion: (internalId: string) => void;
}

const pickedOptionTextSx: SxProps<Theme> = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const actionButtonSx: SxProps<Theme> = { minWidth: 64, height: 30 };

export const SummaryOptionRow = ({ item, pickedOptionId, onViewDetails, onOpenAccordion }: IProps) => {
  const { internalId, name, sku_options } = item;

  const pickedOption = useMemo(
    () => (pickedOptionId ? sku_options?.find((option) => option.id === pickedOptionId) : undefined),
    [sku_options, pickedOptionId],
  );

  const handleViewDetails = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      if (pickedOption) {
        onViewDetails(pickedOption);
      }
    },
    [onViewDetails, pickedOption],
  );

  const handleOpenAccordion = useCallback(() => onOpenAccordion(internalId), [onOpenAccordion, internalId]);

  const handleChooseClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      handleOpenAccordion();
    },
    [handleOpenAccordion],
  );

  const body = useMemo(
    () => (
      <Typography variant="body2" color="text.secondary" sx={pickedOptionTextSx}>
        {pickedOption ? pickedOption.name : NOT_SELECTED_TEXT}
      </Typography>
    ),
    [pickedOption],
  );

  const action = useMemo(
    () =>
      pickedOption ? (
        <Button variant="text" size="small" color="primary" onClick={handleViewDetails} sx={actionButtonSx}>
          View details
        </Button>
      ) : (
        <Button variant="text" size="small" color="primary" onClick={handleChooseClick} sx={actionButtonSx}>
          Choose
        </Button>
      ),
    [pickedOption, handleViewDetails, handleChooseClick],
  );

  return (
    <Box>
      <HorizontalCard
        image_url={pickedOption?.image_url}
        name={name}
        isThumbnailEmpty={!pickedOption}
        hasThumbnailBorder
        body={body}
        onClick={handleOpenAccordion}
        ariaLabel={`${pickedOption ? 'Edit' : 'Choose'} options for ${name}`}
      >
        {action}
      </HorizontalCard>
    </Box>
  );
};
